import type { FC } from 'react';
import { useState } from 'react';
import type { BeneficialOwnerResponse, IANATimezones } from '@gcv/shared';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import { Box, Checkbox } from '@mui/material';

import { useCrbBeneficialOwners, useCrbConfirmBeneficialOwner } from 'tanstack-hooks/crb-beneficial-owners';
import { Button, Text, Toggle, Tooltip } from 'ui';
import { CrbBeneficialOwnerDrawer } from 'ui/apps/crb/company-profile/components/crb-beneficial-owner-drawer';
import { CrbBeneficialOwnerModal } from 'ui/apps/crb/company-profile/components/crb-beneficial-owner-modal';
import { isBeneficialOwnerSelected } from 'ui/apps/crb/inbox/util/questionnaire-edit';
import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import { ConfirmLowThresholdBeneficialOwnerModal } from './confirm-low-threshold-beneficial-owner-modal';
import { getBeneficialOwnerQuestionnaireRowBlurb } from './util/get-beneficial-owner-questionnaire-row-blurb';

interface Props {
  crbId: string;
  templateResultId: string;
  thresholdPercentage: number;
  timezone: IANATimezones;
  questionnaireBankId?: string;
  selectedBeneficialOwnerIds: string[];
  serviceProviderName: string;
  fieldWidth?: string | { xs?: string; sm?: string; md?: string; lg?: string };
}

export const CustomFormBeneficialOwnerRows: FC<Props> = ({
  crbId,
  templateResultId,
  thresholdPercentage,
  timezone,
  questionnaireBankId,
  selectedBeneficialOwnerIds,
  serviceProviderName,
  fieldWidth
}) => {
  const [isAddOwnerOpen, setIsAddOwnerOpen] = useState(false);
  const [viewedOwnerId, setViewedOwnerId] = useState<string>();
  const [showArchived, setShowArchived] = useState(false);
  const [ownerPendingCertification, setOwnerPendingCertification] = useState<BeneficialOwnerResponse>();
  const { beneficialOwners, isLoadingBeneficialOwners } = useCrbBeneficialOwners(crbId);
  const { confirmBeneficialOwner } = useCrbConfirmBeneficialOwner(crbId);

  const viewedOwner = beneficialOwners.find((owner) => owner.id === viewedOwnerId);
  const ownersMeetingThreshold: BeneficialOwnerResponse[] = [];
  const archivedOwners: BeneficialOwnerResponse[] = [];
  const lowThresholdOwners: BeneficialOwnerResponse[] = [];
  for (const bo of beneficialOwners) {
    if (bo.archived) {
      archivedOwners.push(bo);
    } else if (bo.ownership_in_this_business >= thresholdPercentage) {
      ownersMeetingThreshold.push(bo);
    } else {
      lowThresholdOwners.push(bo);
    }
  }
  const otherOwners = [...archivedOwners, ...lowThresholdOwners];
  const visibleOtherOwners = otherOwners.filter((owner) => showArchived || !owner.archived);

  const confirmOwnerSelection = (owner: BeneficialOwnerResponse, confirmation: boolean) => {
    confirmBeneficialOwner({
      beneficialOwnerId: owner.id,
      templateResultId,
      confirmation
    });
  };

  const handleOwnerCreated = (owner: BeneficialOwnerResponse) => {
    if (owner.ownership_in_this_business >= thresholdPercentage) {
      confirmOwnerSelection(owner, true);
    }
  };

  const toggleOwnerSelected = (owner: BeneficialOwnerResponse) => {
    const isAlreadySelected = isBeneficialOwnerSelected(owner, templateResultId, thresholdPercentage);
    if (!isAlreadySelected && owner.ownership_in_this_business < thresholdPercentage) {
      setOwnerPendingCertification(owner);
      return;
    }

    confirmOwnerSelection(owner, !isAlreadySelected);
  };

  const renderOwnerRow = (owner: BeneficialOwnerResponse) => (
    <Box
      key={owner.id}
      onClick={() => setViewedOwnerId(owner.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setViewedOwnerId(owner.id);
        }
      }}
      role="button"
      sx={{
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        gap: 2,
        minHeight: '72px',
        padding: '10px 24px',
        width: '100%',
        '&:not(:last-of-type)': {
          borderBottom: `1px solid ${palette.border}`
        },
        '&:hover': {
          backgroundColor: palette.background.default
        }
      }}
      tabIndex={0}
    >
      <Tooltip title="Archived owners cannot be submitted" disabled={!owner.archived}>
        <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <Checkbox
            checked={selectedBeneficialOwnerIds.includes(owner.id)}
            data-cy={`beneficial-owner-checkbox-${owner.id}`}
            disabled={owner.archived}
            onChange={() => toggleOwnerSelected(owner)}
          />
        </span>
      </Tooltip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Text
            content={[
              owner.beneficial_owner_first_name,
              owner.middle_initial,
              owner.beneficial_owner_last_name
            ]
              .filter(Boolean)
              .join(' ')}
            type="body1"
            sx={{ fontWeight: 700 }}
          />
          {getBeneficialOwnerQuestionnaireRowBlurb(owner)}
        </Box>
        <Text
          content={`${
            owner.job_title_occupation || 'Job title not provided'
          } · DOB ${DateTimeHelpers.formatDateStringToFriendlyDateString(owner.date_of_birth, timezone)}`}
          type="body2"
          sx={{ color: palette.text.secondary, marginTop: '2px' }}
        />
      </Box>
      <Text
        content={`${owner.ownership_in_this_business}%`}
        type="body1"
        sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
      />
      <ArrowForwardIos sx={{ color: palette.text.disabled, fontSize: '18px' }} />
    </Box>
  );

  if (isLoadingBeneficialOwners) {
    return (
      <Box>
        <Text content="Loading beneficial owners..." type="body2" />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Box sx={{ typography: 'body2' }}>
            Certify each owner at {thresholdPercentage}% or more by checking the box, and add anyone who's
            missing. Owners added here and unchanged owners from your last submission are pre-checked.{' '}
            <Box component="span" sx={{ fontStyle: 'italic' }}>
              At least one certified owner is required to submit.
            </Box>
          </Box>
          <Text content="Owners Meeting Threshold" type="table-header" />
          {ownersMeetingThreshold.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${palette.divider}`,
                borderRadius: '4px',
                overflow: 'hidden',
                width: fieldWidth
              }}
            >
              {[...ownersMeetingThreshold]
                .sort((a, b) => b.ownership_in_this_business - a.ownership_in_this_business)
                .map(renderOwnerRow)}
            </Box>
          )}
          <Box sx={{ width: fieldWidth }}>
            <Button
              width="100%"
              color="secondary-outlined"
              label="+ Add Beneficial Owner"
              onClick={() => setIsAddOwnerOpen(true)}
            />
          </Box>
        </Box>
        {(archivedOwners.length > 0 || lowThresholdOwners.length > 0) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Text content="Other Owners" type="table-header" sx={{ mb: 1 }} />
            <Text
              content={`Owners below ${thresholdPercentage}% don't need to be certified here. If one of them is your controlling or managing individual, select them in the section below.`}
              type="body2"
              sx={{ mb: 1 }}
            />
            {archivedOwners.length > 0 && (
              <Box sx={{ width: fieldWidth, mb: 0.5 }}>
                <Toggle
                  checked={showArchived}
                  disableMargin
                  onClick={(event) => setShowArchived(event.target.checked)}
                  label="Show archived"
                />
              </Box>
            )}

            {visibleOtherOwners.length > 0 && (
              <Box
                sx={{
                  border: `1px solid ${palette.divider}`,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  width: fieldWidth
                }}
              >
                {visibleOtherOwners.map(renderOwnerRow)}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <CrbBeneficialOwnerModal
        isOpen={isAddOwnerOpen}
        onClose={() => setIsAddOwnerOpen(false)}
        onOwnerCreated={handleOwnerCreated}
        questionnaireBankId={questionnaireBankId}
        config={{ addOrEdit: 'add', owner: undefined }}
      />
      {viewedOwner && (
        <CrbBeneficialOwnerDrawer
          open={!!viewedOwner}
          onClose={() => setViewedOwnerId(undefined)}
          owner={viewedOwner}
          questionnaireBankId={questionnaireBankId}
        />
      )}
      {ownerPendingCertification && (
        <ConfirmLowThresholdBeneficialOwnerModal
          isOpen={!!ownerPendingCertification}
          onClose={() => setOwnerPendingCertification(undefined)}
          onConfirm={() => {
            confirmOwnerSelection(ownerPendingCertification, true);
            setOwnerPendingCertification(undefined);
          }}
          owner={ownerPendingCertification}
          serviceProviderName={serviceProviderName}
          thresholdPercentage={thresholdPercentage}
        />
      )}
    </>
  );
};
